import { DestroyRef, inject, Injectable } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { getStepEvent } from './step-event.helper';

/**
 * Base service to track view page events when the user navigates to a new page.
 * This service can be used by both QT Lite and Online QT applications.
 *
 * @example
 * // In your app initialization:
 * viewPageTrackingService.startTrackingViewPageEvents();
 */
@Injectable({
  providedIn: 'root',
})
export class ViewPageTrackingService {
  protected readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  protected readonly router = inject(Router);
  protected readonly route = inject(ActivatedRoute);
  protected readonly destroyRef = inject(DestroyRef);

  /**
   * Starts tracking view page events by subscribing to router navigation events.
   * When navigation completes, it will track the Google Analytics event associated with the step.
   */
  startTrackingViewPageEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const event = getStepEvent(this.route);
        if (event) {
          this.googleAnalyticsService.trackViewEvent(event);
        }
      });
  }
}
